// @ts-check

import { deletePersonalAccessToken, getPersonalAccessToken } from '@/api/oauth'
import MobilePlatformVersionDialog from '@/components/mobile/MobilePlatformVersionDialog.vue'
import MobileWrapperVersionDialog from '@/components/mobile/MobileWrapperVersionDialog.vue'
import { useDialog } from '@/core/composables/useDialog'
import {
  LOCAL_STORAGE_KEY_ACCESS_TOKEN,
  LOCAL_STORAGE_KEY_COMPANY_ID,
  LOCAL_STORAGE_KEY_MOBILE_PLATFORM,
  LOCAL_STORAGE_KEY_MOBILE_PLATFORM_VERSION,
  LOCAL_STORAGE_KEY_MOBILE_USER,
  LOCAL_STORAGE_KEY_MOBILE_VERSION,
  LOCAL_STORAGE_KEY_MOBILE_VERSION_REMIND_LATER_EXPIRATION,
  LOCAL_STORAGE_KEY_PERSONAL_ACCESS_TOKEN_ID,
  LOCAL_STORAGE_KEY_SESSION_EXPIRATION_TIME
} from '@/core/constants'
import { setMobileFlag } from '@/helpers/marketing'
import { postNativeMessage } from '@/helpers/mobile'
import * as Sentry from '@sentry/vue'
import { addDays, format, isAfter } from 'date-fns'

/** @param {string|boolean|null} value */
function convertToBoolean(value) {
  if (value === 'true' || value === 'false') {
    return JSON.parse(value)
  }
  return value
}

function parseVersion(version) {
  return version.split('.').map(Number)
}

/** @returns {MobileAppModuleState} */
export const initMobileState = () => ({
  isMobileWrapperUser:
    convertToBoolean(localStorage.getItem(LOCAL_STORAGE_KEY_MOBILE_USER)) ??
    false,
  personalAccessTokenId: localStorage.getItem(
    LOCAL_STORAGE_KEY_PERSONAL_ACCESS_TOKEN_ID
  ),
  mobileWrapperVersion: null,
  mobileWrapperPlatform: null,
  mobileWrapperPlatformVersion: null,
  minimumAllowedMobileWrapperVersion: null,
  minimumAllowedAndroidVersion: '12',
  minimumAllowedIosVersion: '17.5',
  mobileVersionRemindLaterExpired: false
})

/** @type {MobileAppGetterTree} */
export const mobileGetters = {
  isMobileWrapperUser: (state) => state.isMobileWrapperUser,
  mobileWrapperPlatform: (state) => state.mobileWrapperPlatform,
  mobileWrapperPlatformVersion: (state) => state.mobileWrapperPlatformVersion,
  personalAccessTokenId: (state) => state.personalAccessTokenId,
  mobileWrapperVersion: (state) => state.mobileWrapperVersion,
  minimumAllowedMobileWrapperVersion: (state) =>
    state.minimumAllowedMobileWrapperVersion,
  minimumAllowedAndroidVersion: (state) => state.minimumAllowedAndroidVersion,
  minimumAllowedIosVersion: (state) => state.minimumAllowedIosVersion,
  mobileVersionRemindLaterExpired() {
    /*
     * Check if there's localstorage item for the expiration - if not return true
     * IF it's found but it's expired return true
     */
    const expirationTimeValue = localStorage.getItem(
      LOCAL_STORAGE_KEY_MOBILE_VERSION_REMIND_LATER_EXPIRATION
    )

    if (!expirationTimeValue) {
      return true
    }

    const today = new Date()
    const expirationTime = format(new Date(expirationTimeValue), 'yyyy-MM-dd')

    return isAfter(today, expirationTime)
  },
  isAndroid: (state) => {
    return (state.mobileWrapperPlatform?.toLowerCase() ?? '') === 'android'
  },
  isIos: (state) => {
    return (state.mobileWrapperPlatform?.toLowerCase() ?? '') === 'ios'
  },
  isMobileAppAndIos: (state, getters) => {
    return getters.isMobileWrapperUser && getters.isIos
  },
  isMinimumAllowedWrapperVersion: (state) => {
    const { mobileWrapperVersion, minimumAllowedMobileWrapperVersion } = state

    // If for some reason we don't get the version from the wrapper let's force an update
    if (!mobileWrapperVersion || !minimumAllowedMobileWrapperVersion) {
      return false
    }

    const currentVersion = mobileWrapperVersion
    const minimumVersion = minimumAllowedMobileWrapperVersion

    const current = parseVersion(currentVersion)
    const min = parseVersion(minimumVersion)
    const maxLength = Math.max(current.length, min.length, 3)

    while (current.length < maxLength) current.push(0)
    while (min.length < maxLength) min.push(0)

    for (let i = 0; i < maxLength; i += 1) {
      if (current[i] < min[i]) {
        return false
      }
      if (current[i] > min[i]) {
        return true
      }
    }
    return true
  },
  isMinimumAllowedPlatformVersion: (state, getters) => {
    const {
      minimumAllowedAndroidVersion,
      minimumAllowedIosVersion,
      mobileWrapperPlatformVersion
    } = state

    // If for some reason we don't get the version from the wrapper let's not show dialog
    if (!mobileWrapperPlatformVersion) {
      return true
    }

    const currentVersion = mobileWrapperPlatformVersion
    const minimumVersion = getters.isAndroid
      ? minimumAllowedAndroidVersion
      : minimumAllowedIosVersion

    const current = parseVersion(currentVersion)
    const min = parseVersion(minimumVersion)
    const maxLength = Math.max(current.length, min.length, 3)

    while (current.length < maxLength) current.push(0)
    while (min.length < maxLength) min.push(0)

    for (let i = 0; i < maxLength; i += 1) {
      if (current[i] < min[i]) {
        return false
      }
      if (current[i] > min[i]) {
        return true
      }
    }
    return true
  }
}

/** @type {MobileAppMutationTree} */
export const mobileMutations = {
  setIsMobileWrapperUser(state, isMobile) {
    state.isMobileWrapperUser = isMobile
    localStorage.setItem(LOCAL_STORAGE_KEY_MOBILE_USER, `${isMobile}`)
  },
  setMobileWrapperPlatform(state, plat) {
    state.mobileWrapperPlatform = plat
    localStorage.setItem(LOCAL_STORAGE_KEY_MOBILE_PLATFORM, plat)
  },
  setMobileWrapperPlatformVersion(state, platver) {
    // Safety check not to accidentally show the platform dialog
    if (!platver || platver === 'null') {
      return
    }
    state.mobileWrapperPlatformVersion = platver
    localStorage.setItem(LOCAL_STORAGE_KEY_MOBILE_PLATFORM_VERSION, platver)
  },
  setPersonalAccessTokenId(state, id) {
    state.personalAccessTokenId = id
    localStorage.setItem(LOCAL_STORAGE_KEY_PERSONAL_ACCESS_TOKEN_ID, id)
  },
  setMobileWrapperVersion(state, ver) {
    state.mobileWrapperVersion = ver
    localStorage.setItem(LOCAL_STORAGE_KEY_MOBILE_VERSION, ver)
  },
  setMinimumAllowedMobileWrapperVersion(state, ver) {
    state.minimumAllowedMobileWrapperVersion = ver
  },
  clearAllData(state) {
    state.personalAccessTokenId = null
    localStorage.removeItem(LOCAL_STORAGE_KEY_PERSONAL_ACCESS_TOKEN_ID)
  }
}

/** @type {MobileAppActionTree} */
export const mobileActions = {
  async removePersonalAccessToken({ commit }, { patId, accessToken }) {
    try {
      commit('clearAllData')
      await deletePersonalAccessToken(patId, accessToken)
      return Promise.resolve()
    } catch (e) {
      commit('clearAllData')
      return Promise.reject(e.response)
    }
  },
  setMobileWrapperLanguage({ state }, language) {
    if (!state.isMobileWrapperUser) {
      return
    }

    const message = JSON.stringify({
      type: 'ukkopro_set_language',
      message: language
    })

    postNativeMessage(message)
  },
  setMobileWrapperTokens() {
    const message = JSON.stringify({
      type: 'ukkopro_set_tokens',
      message: {
        access_token: localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_TOKEN),
        personal_access_token: localStorage.getItem(
          LOCAL_STORAGE_KEY_PERSONAL_ACCESS_TOKEN_ID
        ),
        expiration_time: localStorage.getItem(
          LOCAL_STORAGE_KEY_SESSION_EXPIRATION_TIME
        ),
        company_id: localStorage.getItem(LOCAL_STORAGE_KEY_COMPANY_ID)
      }
    })
    postNativeMessage(message)
  },
  clearMobileWrapperTokens() {
    const message = JSON.stringify({
      type: 'ukkopro_clear_tokens',
      message: 'clear'
    })
    postNativeMessage(message)
  },
  setAllowedExternalUrls() {
    const message = JSON.stringify({
      type: 'ukkopro_set_urls',
      message: {
        open_self_urls: [
          'pagesense.io',
          'monext.fr',
          'oauth.izettle.com',
          'oauth.zettle.com',
          'onboarding.swan.io',
          'identity.swan.io',
          'swan.io/consent',
          'swan.io/oauth2',
          'stripe.com',
          'hcaptcha.com',
          'api.sumup.com'
        ]
      }
    })
    postNativeMessage(message)
  },
  updateVersionReminderExpiration() {
    localStorage.setItem(
      LOCAL_STORAGE_KEY_MOBILE_VERSION_REMIND_LATER_EXPIRATION,
      format(addDays(new Date(), 14), 'yyyy-MM-dd')
    )
  },
  checkMinimumAllowedMobileWrapperVersion({ commit, getters, rootGetters }) {
    const { showDialog } = useDialog()
    /*
     * Here you can set minimum allowed mobile wrapper version per service
     * If different values needed for Android and iOS then just add
     * minimumAllowedVersion = isAndroid ? '1.0.1' : '1.0.0'
     * */
    let minimumAllowedVersion = '1.1.4'

    if (rootGetters['app/isTmiService']) {
      minimumAllowedVersion = '1.3.4'
    }

    if (rootGetters['app/isTlpService']) {
      minimumAllowedVersion = '1.2.4'
    }

    if (rootGetters['app/isOyService']) {
      minimumAllowedVersion = '1.1.4'
    }

    commit('setMinimumAllowedMobileWrapperVersion', minimumAllowedVersion)

    if (
      !getters.isMinimumAllowedWrapperVersion &&
      getters.isMinimumAllowedPlatformVersion
    ) {
      showDialog({
        component: MobileWrapperVersionDialog
      })
    }
  },
  checkMinimumAllowedMobilePlatformVersion({ getters, dispatch }) {
    const { showDialog } = useDialog()

    const minimumAllowedPlatformVersion =
      getters.isMinimumAllowedPlatformVersion

    // If is minimum allowed platform version remove the item if exists
    if (minimumAllowedPlatformVersion) {
      localStorage.removeItem(
        LOCAL_STORAGE_KEY_MOBILE_VERSION_REMIND_LATER_EXPIRATION
      )
    }

    if (
      !minimumAllowedPlatformVersion &&
      getters.mobileVersionRemindLaterExpired
    ) {
      showDialog({
        component: MobilePlatformVersionDialog
      })
      dispatch('updateVersionReminderExpiration')
      return
    }

    /*
     * After we know the minimum allowed platform version, we can check the wrapper version
     * This is so that we do not force an app update for customers who can't get it and
     * hence make the app not usable since the wrapper version dialog is persistant
     */
    if (minimumAllowedPlatformVersion) {
      dispatch('checkMinimumAllowedMobileWrapperVersion')
    }
  },
  setMobileFlags({ commit }) {
    setMobileFlag(true)
    commit('setIsMobileWrapperUser', true)
    commit(
      'setMobileWrapperPlatform',
      localStorage.getItem(LOCAL_STORAGE_KEY_MOBILE_PLATFORM)
    )
    commit(
      'setMobileWrapperPlatformVersion',
      localStorage.getItem(LOCAL_STORAGE_KEY_MOBILE_PLATFORM_VERSION)
    )

    const mobileWrapperVersion =
      localStorage.getItem(LOCAL_STORAGE_KEY_MOBILE_VERSION) ?? ''
    const versionNumberRegEx = /\d{1,2}\.\d{1,2}\.\d{1,3}/
    const isVersionNumber = versionNumberRegEx.test(mobileWrapperVersion)
    const versionNumber = isVersionNumber ? mobileWrapperVersion : null

    commit('setMobileWrapperVersion', versionNumber)
  },
  setMobileSentryConfig({ state }) {
    Sentry.getCurrentScope().setUser({
      mobile_application: true,
      mobile_version: state.mobileWrapperVersion,
      mobile_platform: state.mobileWrapperPlatform,
      mobile_platform_version: state.mobileWrapperPlatformVersion,
      mobile_minimum_version_set: state.minimumAllowedMobileWrapperVersion
    })
  },
  async checkMobileAuthTokens({
    rootState,
    rootGetters,
    getters,
    state,
    commit,
    dispatch
  }) {
    if (
      !rootState.auth.accessToken ||
      !rootState.auth.sessionExpirationTime ||
      !rootState.auth.companyId ||
      !state.personalAccessTokenId
    ) {
      const accessToken = localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_TOKEN)

      const sessionExpirationTime = localStorage.getItem(
        LOCAL_STORAGE_KEY_SESSION_EXPIRATION_TIME
      )

      const companyId = localStorage.getItem(LOCAL_STORAGE_KEY_COMPANY_ID)

      const personalAccessToken = convertToBoolean(
        localStorage.getItem(LOCAL_STORAGE_KEY_PERSONAL_ACCESS_TOKEN_ID)
      )

      if (accessToken && sessionExpirationTime && companyId) {
        commit('auth/setAccessToken', accessToken, { root: true })
        commit('auth/setSessionExpirationTime', sessionExpirationTime, {
          root: true
        })
        commit('auth/setCompanyId', companyId, { root: true })

        if (!personalAccessToken) {
          try {
            const { data } = await getPersonalAccessToken(
              `mobile_pat_${rootGetters['companyData/companyId']}`,
              accessToken
            )
            commit('setPersonalAccessTokenId', data.token.id)
          } catch (error) {
            console.error('Failed to get personal access token:', error)
            await dispatch('auth/logout', null, { root: true })
          }
        }
      } else {
        // Prevent closing dialog if not logged in
        if (!getters.isMinimumAllowedWrapperVersion) {
          return
        }

        await dispatch('auth/logout', null, { root: true })
      }
    }
  },
  async checkMobileStatus({ dispatch }) {
    const isMobileWrapperUser = convertToBoolean(
      localStorage.getItem(LOCAL_STORAGE_KEY_MOBILE_USER) ?? 'false'
    )

    // Mobile wrapper sets local storage items
    if (isMobileWrapperUser) {
      await dispatch('setMobileFlags')
      await dispatch('setMobileSentryConfig')
      await dispatch('checkMobileAuthTokens')
      await dispatch('checkMinimumAllowedMobilePlatformVersion')

      // Timeout for cleaner showing of app content
      const message = JSON.stringify({
        type: 'ukkopro_frontend_loaded'
      })

      postNativeMessage(message)
    } else {
      setMobileFlag(false)
    }
  }
}

export const mobileModule = {
  namespaced: true,
  state: initMobileState,
  getters: mobileGetters,
  mutations: mobileMutations,
  actions: mobileActions
}

export default mobileModule
